import React, {
    useEffect,
    useState,
} from 'react';

import {
    useTranslation,
} from 'react-i18next';

import AdminInformationForm
    from './AdminInformationForm';

import PolicyAndPermissionForm
    from './PolicyAndPermissionForm';

import {
    getSalesRepresentativeList,
} from '../../services/signupService';
import { getRegionalSalesAdminId } from '../../services/regionalSalesRouting';

const KOREAN_ADMIN_GROUP_PRIORITY = [
    '국내영업 - 중부',
    '국내영업 - 남부',
    'SW Dev',
];

const GLOBAL_ADMIN_GROUP_PRIORITY = [
    'overseas sales',
];

const INITIAL_FORM = {
    siteName: '',
    loginId: '',
    password: '',
    repassword: '',
    email: '',

    address: '',
    country: '',
    position: null,
    placeID: '',

    sales: '',
};


export default function SignupMergedForm({
    onSubmit,
    isSubmitting,
}) {
    const {
        t,
        i18n,
    } = useTranslation(
        'app',
    );

    const [
        currentStep,
        setCurrentStep,
    ] = useState(1);

    const [
        form,
        setForm,
    ] = useState(
        INITIAL_FORM,
    );

    const [
        salesRepOptions,
        setSalesRepOptions,
    ] = useState([]);

    const [
        isLoadingSalesRep,
        setIsLoadingSalesRep,
    ] = useState(false);

    const [
        policyAgreed1,
        setPolicyAgreed1,
    ] = useState(false);

    const [
        policyAgreed2,
        setPolicyAgreed2,
    ] = useState(false);

    const [
        isLoginIdDuplicated,
        setIsLoginIdDuplicated,
    ] = useState(false);

    const [
        message,
        setMessage,
    ] = useState('');

    useEffect(
        () => {
            let mounted =
                true;

            const loadSalesRepresentatives =
                async () => {
                    setIsLoadingSalesRep(
                        true,
                    );

                    try {
                        const list =
                            await getSalesRepresentativeList();

                        if (
                            !mounted
                        ) {
                            return;
                        }

                        const currentLanguage =
                            String(
                                i18n.resolvedLanguage ||
                                i18n.language ||
                                '',
                            )
                                .toLowerCase();

                        const isKorean =
                            currentLanguage.startsWith(
                                'ko',
                            );

                        const groupPriority =
                            isKorean
                                ? KOREAN_ADMIN_GROUP_PRIORITY
                                : GLOBAL_ADMIN_GROUP_PRIORITY;

                        const filteredList =
                            list.filter(
                                (
                                    representative,
                                ) => {
                                    const admin_group =
                                        String(
                                            representative.admin_group ||
                                            '',
                                        ).trim();

                                    return groupPriority.includes(
                                        admin_group,
                                    ) || admin_group.toLowerCase() === 'overseas sales';
                                },
                            );

                        const sortedList =
                            [...filteredList]
                                .sort(
                                    (
                                        a,
                                        b,
                                    ) => {
                                        const groupA =
                                            String(
                                                a.admin_group ||
                                                '',
                                            ).trim();

                                        const groupB =
                                            String(
                                                b.admin_group ||
                                                '',
                                            ).trim();

                                        const priorityA =
                                            groupPriority.indexOf(
                                                groupA,
                                            );

                                        const priorityB =
                                            groupPriority.indexOf(
                                                groupB,
                                            );


                                        if (
                                            priorityA !==
                                            priorityB
                                        ) {
                                            return (
                                                priorityA -
                                                priorityB
                                            );
                                        }


                                        return String(
                                            a.name ||
                                            '',
                                        ).localeCompare(
                                            String(
                                                b.name ||
                                                '',
                                            ),
                                            isKorean
                                                ? 'ko'
                                                : 'en',
                                        );
                                    },
                                );


                        const options =
                            sortedList
                                .map(
                                    (
                                        representative,
                                    ) => {
                                        const id =
                                            representative.id;

                                        if (
                                            id === undefined ||
                                            id === null ||
                                            id === ''
                                        ) {
                                            return null;
                                        }


                                        const name =
                                            representative.name ||
                                            '';

                                        const email =
                                            representative.email ||
                                            '';

                                        const phone =
                                            representative.phone ||
                                            '';

                                        const admin_group =
                                            representative.admin_group ||
                                            '';


                                        return {
                                            value:
                                                String(
                                                    id,
                                                ),

                                            label:
                                                email
                                                    ? `[${admin_group}] ${name} (${email})`
                                                    : `[${admin_group}] ${name}`,

                                            managerSeq:
                                                id,

                                            managerName:
                                                name,

                                            managerEmail:
                                                email,

                                            managerPhone:
                                                phone,

                                            admin_group:
                                                admin_group,

                                            admin_id:
                                                representative.admin_id || '',

                                        };
                                    },
                                )
                                .filter(
                                    Boolean,
                                );


                        setSalesRepOptions(
                            options,
                        );
                    } catch (
                    error
                    ) {
                        console.error(
                            '[SignupMergedForm] Failed to load sales representatives:',
                            error,
                        );

                        if (
                            mounted
                        ) {
                            setSalesRepOptions(
                                [],
                            );
                        }
                    } finally {
                        if (
                            mounted
                        ) {
                            setIsLoadingSalesRep(
                                false,
                            );
                        }
                    }
                };


            loadSalesRepresentatives();


            return () => {
                mounted =
                    false;
            };
        },
        [
            i18n.resolvedLanguage,
            i18n.language,
        ],
    );

    const currentLanguage = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase();
    const isKorean = currentLanguage.startsWith('ko');
    const isDomesticManualMode = isKorean && String(form.country || '').toUpperCase() === 'KR';
    const regionalAdminId = getRegionalSalesAdminId(form.country);
    const routedSalesRepresentative = regionalAdminId
        ? salesRepOptions.find((option) => option.admin_id === regionalAdminId && String(option.admin_group || '').trim().toLowerCase() === 'overseas sales')
        : null;
    const salesRoleUnavailable = !isDomesticManualMode && Boolean(form.country) && !isLoadingSalesRep && !routedSalesRepresentative;

    useEffect(() => {
        if (isDomesticManualMode) {
            setForm((previous) => previous.sales ? { ...previous, sales: '' } : previous);
            return;
        }
        const nextSales = routedSalesRepresentative?.value || '';
        setForm((previous) => previous.sales === nextSales ? previous : { ...previous, sales: nextSales });
    }, [isDomesticManualMode, routedSalesRepresentative?.value]);

    const handleChange =
        (
            name,
            value,
        ) => {
            setForm(
                (
                    previous,
                ) => ({
                    ...previous,
                    [name]:
                        value,
                }),
            );

            if (
                name ===
                'loginId'
            ) {
                setIsLoginIdDuplicated(
                    false,
                );
            }

            setMessage(
                '',
            );
        };

    const handleNextStep =
        () => {
            if (
                !policyAgreed1 ||
                !policyAgreed2
            ) {
                setMessage(
                    t(
                        'signup.agreement.requiredMessage',
                        {
                            defaultValue:
                                '정책에 동의해야 합니다.',
                        },
                    ),
                );

                return;
            }

            setMessage(
                '',
            );

            setCurrentStep(
                2,
            );
        };


    const handlePreviousStep =
        () => {
            setMessage(
                '',
            );

            setCurrentStep(
                1,
            );
        };

    const handleSubmit =
        async () => {
            if (
                !policyAgreed1 ||
                !policyAgreed2
            ) {
                setMessage(
                    t(
                        'signup.agreement.requiredMessage',
                        {
                            defaultValue:
                                '정책에 동의해야 합니다.',
                        },
                    ),
                );

                return;
            }

            const selectedManager =
                salesRepOptions.find(
                    (
                        option,
                    ) =>
                        String(
                            option.value,
                        ) ===
                        String(
                            form.sales,
                        ),
                );

            const mergedValues = {
                ...form,

                ...(
                    selectedManager ||
                    {}
                ),

                policyAgreement1:
                    policyAgreed1,

                policyAgreement2:
                    policyAgreed2,

                policyAgreement:
                    policyAgreed1 &&
                    policyAgreed2,

                grade:
                    0,
            };

            await onSubmit?.(
                mergedValues,
            );
        };

    return (
        <div className="signup-form">
            <div className="signup-step-indicator">
                <div
                    className={
                        currentStep >= 1
                            ? 'signup-step active'
                            : 'signup-step'
                    }
                >
                    <span>
                        1
                    </span>

                    <strong>
                        {t(
                            'signup.steps.agreement',
                            {
                                defaultValue:
                                    '약관 동의',
                            },
                        )}
                    </strong>
                </div>

                <div className="signup-step-line" />

                <div
                    className={
                        currentStep >= 2
                            ? 'signup-step active'
                            : 'signup-step'
                    }
                >
                    <span>
                        2
                    </span>

                    <strong>
                        {t(
                            'signup.steps.information',
                            {
                                defaultValue:
                                    '정보 입력',
                            },
                        )}
                    </strong>
                </div>
            </div>


            {message && (
                <div
                    className="signup-form-error"
                    role="alert"
                >
                    {
                        message
                    }
                </div>
            )}


            {currentStep === 1 && (
                <PolicyAndPermissionForm
                    policyAgreed1={
                        policyAgreed1
                    }
                    setPolicyAgreed1={
                        setPolicyAgreed1
                    }
                    policyAgreed2={
                        policyAgreed2
                    }
                    setPolicyAgreed2={
                        setPolicyAgreed2
                    }
                    onNext={
                        handleNextStep
                    }
                />
            )}


            {currentStep === 2 && (
                <AdminInformationForm
                    form={
                        form
                    }
                    onChange={
                        handleChange
                    }
                    salesRepOptions={
                        salesRepOptions.filter((option) => KOREAN_ADMIN_GROUP_PRIORITY.includes(option.admin_group))
                    }
                    salesAutoMode={!isDomesticManualMode}
                    salesRoleUnavailable={salesRoleUnavailable}
                    isLoadingSalesRep={
                        isLoadingSalesRep
                    }
                    isLoginIdDuplicated={
                        isLoginIdDuplicated
                    }
                    setIsLoginIdDuplicated={
                        setIsLoginIdDuplicated
                    }
                    onBack={
                        handlePreviousStep
                    }
                    onNext={
                        handleSubmit
                    }
                    isSubmitting={
                        isSubmitting
                    }
                    setMessage={
                        setMessage
                    }
                />
            )}
        </div>
    );
}
